import { authTimeouts, refreshSession } from 'AuthBff/auth-fetch';
import { getAuthBaseUrl } from 'AuthBff/env';
import { consumeFreshCentralSession, SSO_ATTEMPTED_KEY } from 'AuthBff/sso-rearm';
import type { AuthState, AuthUser, TenantSummary } from 'AuthBff/types';
import { boundedFetch } from 'AuthSdk/bounded-fetch';

export type SessionBootOptions = {
  isCancelled?: () => boolean;
  meUrl?: string;
  navigate?: (path: string) => void;
  oidc?: boolean;
  onSilentSignIn?: () => void;
  silentSso?: boolean;
};

const SILENT_FRAME_ATTRIBUTE = 'data-bomdisoft-silent-sso';
const SILENT_FRAME_TIMEOUT_MS = 15_000;

const claimSilentAttempt = (): boolean => {
  try {
    if (window.sessionStorage.getItem(SSO_ATTEMPTED_KEY) && !consumeFreshCentralSession()) {
      return false;
    }
    window.sessionStorage.setItem(SSO_ATTEMPTED_KEY, '1');
    return true;
  } catch {
    return false;
  }
};

export const attemptFramedSilentSso = ({ onDone, timeoutMs = SILENT_FRAME_TIMEOUT_MS }: { onDone: (signedIn: boolean) => void; timeoutMs?: number }): boolean => {
  if (typeof window === 'undefined' || !claimSilentAttempt()) {
    return false;
  }
  const frame = document.createElement('iframe');
  let settled = false;
  const settle = (signedIn: boolean) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    frame.remove();
    onDone(signedIn);
  };
  const timer = setTimeout(() => settle(false), timeoutMs);
  frame.addEventListener('load', () => {
    let answer: string | null | undefined;
    try {
      answer = frame.contentDocument?.querySelector('meta[name="bomdisoft-silent-sso"]')?.getAttribute('content');
    } catch {
      return;
    }
    if (answer) settle(answer === 'signed_in');
  });
  frame.hidden = true;
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.setAttribute(SILENT_FRAME_ATTRIBUTE, '');
  frame.setAttribute('src', '/api/auth/login?prompt=none&frame=1');
  document.body.append(frame);
  return true;
};

/**
 * No local session, but the central login may hold one: bounce once through
 * `prompt=none`. The login origin either finalizes from its remembered session
 * (we come back with a code, signed in) or fails the request as
 * `login_required` (we come back exactly as before, and the marker stops any
 * second attempt). An E2E suite that wants no bounce seeds `SSO_ATTEMPTED_KEY`,
 * the state of a tab that already tried; nothing here infers intent from
 * automation (F-11).
 */
export const attemptSilentSso = ({ navigate, oidc = false, silentSso = true }: SessionBootOptions): boolean => {
  if (!silentSso || !oidc || typeof window === 'undefined') {
    return false;
  }
  // An arrival the central login marked as carrying a fresh session is the
  // one moment the spent shot is owed again, however the visitor reached
  // the login form. Consuming the mark keeps it a single extra attempt.
  if (!claimSilentAttempt()) {
    return false;
  }
  const returnTo = `${window.location.pathname}${window.location.search}`;
  const go = navigate ?? (path => window.location.assign(path));
  go(`/api/auth/login?prompt=none&return_to=${encodeURIComponent(returnTo)}`);
  return true;
};

/**
 * Resolve the page's session from the BFF: the refresh cookie, then the signed-in
 * person. Answers `loading` while a silent sign-in bounce is under way, since the
 * page is about to leave.
 */
export const bootSession = async (options: SessionBootOptions = {}): Promise<AuthState> => {
  const { isCancelled = () => false, meUrl = '/api/auth/me', oidc = false } = options;
  try {
    // Shares the auth-fetch single-flight (and its cross-tab lock): the
    // refresh cookie rotates single-use at the issuer, so a boot must never
    // present it concurrently with another consumer's refresh.
    let session = await refreshSession();
    if (!session.accessToken) {
      // One retry before believing it, because "no token" has two causes and
      // only one of them is a signed-out visitor. The rotation dedupe in the
      // BFF is per process and every consumer runs serverless, so two boots
      // racing (or a second tab) can have this call present a generation a
      // peer already spent: the issuer answers invalid_grant and the handler
      // reports unauthenticated even though the WINNER has just set a live
      // successor in the cookie jar. A second call picks that successor up.
      // Without this the loser falls through to silent SSO, bounces to
      // /api/auth/login, comes back, races again, and loops.
      // A genuinely dead session pays one extra request and still lands
      // unauthenticated.
      session = await refreshSession();
    }
    if (!session.accessToken) {
      // A definite "no session" is worth one silent bounce. Infra failures
      // (5xx, auth down) throw instead and must not redirect anywhere:
      // bouncing on an outage strands the visitor.
      const { onSilentSignIn } = options;
      if (onSilentSignIn) {
        if (!isCancelled() && options.silentSso !== false && oidc) attemptFramedSilentSso({ onDone: signedIn => signedIn && !isCancelled() && onSilentSignIn() });
        return { status: 'unauthenticated' };
      }
      return !isCancelled() && attemptSilentSso(options) ? { status: 'loading' } : { status: 'unauthenticated' };
    }
    if (oidc) {
      const meRes = await boundedFetch(meUrl, { headers: { authorization: `Bearer ${session.accessToken}` } }, authTimeouts().meMs);
      if (!meRes.ok) {
        return { status: 'unauthenticated' };
      }
      const me = (await meRes.json()) as { user: AuthUser; activeTenant: TenantSummary | null };
      return { accessToken: session.accessToken, activeTenant: me.activeTenant, idToken: session.idToken ?? null, status: 'authenticated', user: me.user };
    }
    const meRes = await boundedFetch(`${getAuthBaseUrl()}/auth/me`, { headers: { authorization: `Bearer ${session.accessToken}` } }, authTimeouts().meMs);
    if (!meRes.ok) {
      return { status: 'unauthenticated' };
    }
    const user = (await meRes.json()) as AuthUser;
    return { accessToken: session.accessToken, activeTenant: session.activeTenant ?? null, idToken: null, status: 'authenticated', user };
  } catch {
    return { status: 'unauthenticated' };
  }
};
